import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import './Landing.css'

const Landing: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'features' | 'how-it-works' | 'pricing'>('features')

  return (
    <div className="landing">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Split Expenses with Friends, 
            <span className="highlight"> Not Relationships</span>
          </h1>
          <p className="hero-subtitle">
            The frictionless way to track shared expenses and maintain social harmony. 
            No more awkward money conversations or forgotten debts.
          </p>
          <div className="hero-actions">
            <Link to="/group/create" className="btn btn-primary">
              Create Your First Group
            </Link>
            <Link to="/group/join" className="btn btn-secondary">
              Join Existing Group
            </Link>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">10K+</span>
              <span className="stat-label">Groups Created</span>
            </div>
            <div className="stat">
              <span className="stat-number">$2M+</span>
              <span className="stat-label">Expenses Tracked</span>
            </div>
            <div className="stat">
              <span className="stat-number">50K+</span>
              <span className="stat-label">Happy Users</span>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="app-preview">
            <div className="preview-header">
              <div className="preview-dot red"></div>
              <div className="preview-dot yellow"></div>
              <div className="preview-dot green"></div>
            </div>
            <div className="preview-content">
              <div className="mock-group">
                <div className="group-header">
                  <h3>Weekend Trip</h3>
                  <span className="group-balance positive">+$45.50</span>
                </div>
                <div className="expense-list">
                  <div className="expense-item">
                    <span className="expense-desc">Dinner</span>
                    <span className="expense-amount">$32.00</span>
                  </div>
                  <div className="expense-item">
                    <span className="expense-desc">Gas</span>
                    <span className="expense-amount">$28.50</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose VibeCoding?</h2>
            <p>Built for real people who want to split expenses without the drama</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>Transparent Tracking</h3>
              <p>See exactly who owes what in real-time. No more guessing or awkward conversations about money.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">👥</div>
              <h3>Group Harmony</h3>
              <p>Keep friendships intact with fair expense distribution and clear settlement tracking.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Lightning Fast</h3>
              <p>Add expenses in seconds and see balances update instantly across all group members.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Privacy First</h3>
              <p>Your financial data stays private. We don't store sensitive information or share your data.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Mobile Optimized</h3>
              <p>Perfect for on-the-go expense tracking. Works seamlessly on all your devices.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🌐</div>
              <h3>Offline Ready</h3>
              <p>Add expenses even without internet. Everything syncs when you're back online.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Get started in minutes, not hours</p>
          </div>
          
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Create or Join a Group</h3>
                <p>Start a new expense group or join an existing one using a simple invitation link.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Add Expenses</h3>
                <p>Record shared expenses with amounts, descriptions, and who paid. Split equally or customize the distribution.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Track Balances</h3>
                <p>See real-time updates of who owes what to whom. Clear visibility eliminates confusion.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Settle Up</h3>
                <p>Mark debts as paid and maintain a complete history of all settlements and payments.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases">
        <div className="container">
          <div className="section-header">
            <h2>Perfect For</h2>
            <p>Whether you're planning a trip or splitting rent, we've got you covered</p>
          </div>
          
          <div className="use-cases-grid">
            <div className="use-case">
              <div className="use-case-icon">🏠</div>
              <h3>Roommates</h3>
              <p>Split rent, utilities, groceries, and household expenses fairly among roommates.</p>
            </div>
            
            <div className="use-case">
              <div className="use-case-icon">✈️</div>
              <h3>Travel Groups</h3>
              <p>Track shared expenses during trips - accommodation, meals, activities, and transportation.</p>
            </div>
            
            <div className="use-case">
              <div className="use-case-icon">🎉</div>
              <h3>Event Planning</h3>
              <p>Organize group events, parties, and celebrations with transparent cost sharing.</p>
            </div>
            
            <div className="use-case">
              <div className="use-case-icon">💑</div>
              <h3>Couples</h3>
              <p>Manage shared expenses while maintaining separate finances and clear accountability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Simplify Your Expense Splitting?</h2>
            <p>Join thousands of users who've already eliminated the awkwardness of splitting bills with friends.</p>
            <div className="cta-actions">
              <Link to="/group/create" className="btn btn-primary btn-large">
                Get Started Free
              </Link>
              <Link to="/group/join" className="btn btn-ghost btn-large">
                Join Existing Group
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>VibeCoding</h3>
              <p>Making expense splitting frictionless and fun.</p>
            </div>
            
            <div className="footer-links">
              <div className="footer-section">
                <h4>Product</h4>
                <ul>
                  <li><Link to="/group/create">Create Group</Link></li>
                  <li><Link to="/group/join">Join Group</Link></li>
                  <li><a href="#features">Features</a></li>
                  <li><a href="#how-it-works">How It Works</a></li>
                </ul>
              </div>
              
              <div className="footer-section">
                <h4>Support</h4>
                <ul>
                  <li><a href="#help">Help Center</a></li>
                  <li><a href="#contact">Contact Us</a></li>
                  <li><a href="#privacy">Privacy Policy</a></li>
                  <li><a href="#terms">Terms of Service</a></li>
                </ul>
              </div>
              
              <div className="footer-section">
                <h4>Company</h4>
                <ul>
                  <li><a href="#about">About</a></li>
                  <li><a href="#blog">Blog</a></li>
                  <li><a href="#careers">Careers</a></li>
                  <li><a href="#press">Press</a></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2024 VibeCoding. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Landing 